import React, { useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Alert from '../ui/Alert';
import './SendReview.css';

const SendReview = ({ 
  document, 
  recipients = [], 
  currentUser,
  onSend,
  loading = false
}) => {
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };
  
  const handleSend = () => {
    onSend({
      message,
      recipients
    });
  };
  
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };
  
  // Generate colors for recipients
  const getRecipientColor = (index) => {
    const colors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336'];
    return colors[index % colors.length];
  };
  
  // Count fields per recipient
  const getFieldsCount = (recipientEmail) => {
    return document?.fields.filter(field => field.assignedTo === recipientEmail).length || 0;
  };
  
  // Count owner fields
  const ownerFieldsCount = document?.fields.filter(field => field.assignedTo === 'owner').length || 0;
  
  return (
    <div className="send-review">
      <h2 className="send-review-title">Review and Send</h2>
      
      {error && (
        <Alert 
          type="error"
          message={error}
          dismissible={true}
          onDismiss={() => setError(null)}
        />
      )}
      
      <div className="send-review-container">
        <div className="send-review-document">
          <Card className="document-info-card">
            <h3 className="document-info-title">Document Information</h3>
            
            <div className="document-info-content">
              <div className="info-group">
                <div className="info-label">Document Name</div>
                <div className="info-value">{document?.name}</div>
              </div>
              
              <div className="info-group">
                <div className="info-label">Signature Type</div>
                <div className="info-value">
                  {document?.signatureType === 'self' && 'Just Me'}
                  {document?.signatureType === 'me_and_others' && 'Me + Others'}
                  {document?.signatureType === 'others_only' && 'Others Only'}
                </div>
              </div>
              
              <div className="info-group">
                <div className="info-label">Total Signature Fields</div>
                <div className="info-value">{document?.fields.length || 0}</div>
              </div>
            </div>
            
            <div className="document-preview-actions">
              <Button 
                onClick={togglePreview}
                variant="secondary"
              >
                {showPreview ? 'Hide Preview' : 'Show Document Preview'}
              </Button>
            </div>
          </Card>
          
          {showPreview && (
            <div className="document-preview">
              <div className="document-preview-placeholder">
                Document preview would be rendered here
              </div>
            </div>
          )}
        </div>
        
        <div className="send-review-sidebar">
          <Card className="recipients-card">
            <h3 className="recipients-title">Recipients</h3>
            
            {document?.signatureType === 'me_and_others' && (
              <div className="recipient-item owner-item">
                <div className="recipient-info">
                  <div className="recipient-name">You (Owner)</div>
                  <div className="recipient-email">{currentUser?.email}</div>
                  <div className="recipient-fields-count">
                    {ownerFieldsCount} field{ownerFieldsCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="recipient-status">Will sign first</div>
              </div>
            )}
            
            {recipients.map((recipient, index) => {
              const color = getRecipientColor(index);
              const fieldsCount = getFieldsCount(recipient.email);
              
              return (
                <div 
                  key={recipient.email}
                  className="recipient-item"
                  style={{ borderLeftColor: color }}
                >
                  <div className="recipient-info">
                    <div className="recipient-name">{recipient.name}</div>
                    <div className="recipient-email">{recipient.email}</div>
                    <div className="recipient-fields-count">
                      {fieldsCount} field{fieldsCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="recipient-status">
                    {document?.signatureType === 'me_and_others' 
                      ? 'Will sign after you' 
                      : index === 0 ? 'Will sign first' : `Will sign ${index + 1}${index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'}`}
                  </div>
                </div>
              );
            })}
          </Card>
          
          <Card className="message-card">
            <h3 className="message-title">Add a Message</h3>
            
            <div className="message-form">
              <textarea
                className="message-input"
                placeholder="Add a personal message to all recipients (optional)"
                value={message}
                onChange={handleMessageChange}
                rows="4"
              />
            </div>
          </Card>
          
          <div className="send-actions">
            <Button 
              onClick={handleSend}
              disabled={loading}
              loading={loading}
              fullWidth
            >
              {document?.signatureType === 'me_and_others' 
                ? 'Sign & Send to Others' 
                : 'Send for Signing'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendReview;
